import Typography from '@mui/material/Typography'
import { formatNumberAsPercent, formatNumberAsPrice, i18WithDefault as t } from '../shared'
import { ColorChip } from 'color_chip'
import DiscountTagIcon from '@mui/icons-material/Loyalty'
import { useTheme } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import type { AvailabilityPriceDto } from 'service-api/src/types/api'
import { getFilteredDiscountBenefits } from '@shared/availability'

export interface DiscountTagProps {
  discounts: AvailabilityPriceDto['discounts']
  cssOverrides?: SxProps<Theme>
  iconColor?: string
  eligibleForConsumableBenefits?: boolean
}

export const DiscountTag = ({
  discounts,
  cssOverrides,
  iconColor,
  eligibleForConsumableBenefits = false,
}: DiscountTagProps) => {
  const theme = useTheme()
  const filteredDiscountBenefits = getFilteredDiscountBenefits(discounts)

  if (eligibleForConsumableBenefits) {
    return (
      <ColorChip
        icon={<DiscountTagIcon style={{ color: iconColor || theme.palette.secondary.default }} fontSize="small" />}
        label={
          <Typography style={{ fontWeight: 600, fontSize: 12 }}>
            {t('global.label.eligibleForLimitedBenefits')}
          </Typography>
        }
        size="small"
        sxOverrides={{
          borderRadius: 1,
          backgroundColor: theme.palette.background.alert,
          color: theme.palette.warning.dark,
          display: 'flex',
          justifyContent: 'flex-start',
          '& .MuiChip-label': {
            width: '100%',
          },
          '&.MuiChip-root': {
            maxWidth: { xs: '100%', md: '240px' },
            height: '32px',
          },
          ...cssOverrides,
        }}
      />
    )
  }

  return filteredDiscountBenefits && filteredDiscountBenefits.length > 0 ? (
    <ColorChip
      icon={<DiscountTagIcon style={{ color: iconColor || theme.palette.info.light }} fontSize="small" />}
      label={filteredDiscountBenefits.map(({ value, type }, index) => (
        <Typography key={index} style={{ fontWeight: 600, fontSize: 12 }}>
          {type === 'RATE'
            ? formatNumberAsPercent(value! * 100.0, { keepZero: false })
            : formatNumberAsPrice(value!, undefined, undefined, false)}{' '}
          {t('global.label.off').toUpperCase()}
        </Typography>
      ))}
      sxOverrides={{
        borderRadius: 1,
        backgroundColor: theme.palette.background.info,
        color: theme.palette.info.darken60,
        display: 'flex',
        justifyContent: 'flex-start',
        '& .MuiChip-label': {
          width: '100%',
        },
        '&.MuiChip-root': {
          maxWidth: { xs: '100%', md: '240px' },
          height: '32px',
        },
        ...cssOverrides,
      }}
    />
  ) : null
}
